import { EpochLimitStatusResponse } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getAutomaticStreakEpochLimit } from "@/utils/api";
import { QueryClient, useQuery } from "@tanstack/react-query";

/**
 * Invalidates the epoch limit query for a specific campaign and epoch.
 * Can be used outside of the hook (e.g., in mutation callbacks).
 */
export const invalidateEpochLimitQuery = (queryClient: QueryClient, campaignName: string) => {
  queryClient.invalidateQueries({
    queryKey: [QueryKeys.AUTOMATIC_STREAK_CONFIG, "epoch-limit", campaignName],
  });
};

/**
 * Hook to check if the epoch limit for autopilots has been reached.
 *
 * Only fetches when enabled (e.g., when the campaign has maxAutopilotsPerEpoch configured).
 *
 * @param campaignName - The campaign name to check epoch limit for
 * @param epochId - The epoch ID to check the limit for
 * @param enabled - Whether to fetch the epoch limit status (default: true)
 */
export const useAutomaticStreakEpochLimit = (campaignName?: string, epochId?: number, enabled = true) => {
  const { auth, authenticated } = useHubUser();

  const shouldFetch = authenticated && !!campaignName && epochId != null && enabled;

  const query = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAK_CONFIG, "epoch-limit", campaignName, epochId],
    queryFn: async (): Promise<EpochLimitStatusResponse> => {
      const result = await getAutomaticStreakEpochLimit(auth, campaignName!, epochId!);

      if (isServerApiError(result) || isServerApiRfcError(result)) {
        throw new Error("Failed to fetch epoch limit status");
      }

      return result;
    },
    enabled: shouldFetch,
    staleTime: 30 * 1000, // 30 seconds - more frequent for epoch limit checking
    gcTime: 1 * 60 * 1000,
    retry: 2,
  });

  return {
    /** Whether the epoch limit is reached (cannot create more autopilots) */
    isLimitReached: query.data?.isLimitReached ?? false,
    /** Whether the query is loading */
    isLoading: shouldFetch && query.isLoading,
    /** Error message if the query failed */
    error: query.error?.message,
    /** Refetch the epoch limit status */
    refetch: query.refetch,
  };
};
